import React, { useEffect, useState, useContext } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AccountsList } from "./components/accounts-list";
import { useHome } from "@/features/home/hooks/use-home";
import { useHomeStates } from "@/features/home/hooks/use-home-states"
import { useGetAccountGroups } from "@/features/accounts/account-group/hooks/use-get-account-groups";
import { useUpdateAccountGroups } from "@/features/accounts/account-group/hooks/use-update-account-groups";
import { useAccountGroupsContext } from "@/features/accounts/account-group/contexts/account-groups-context";
import { useModalContext } from "@/shared/components/modal/contexts/modal-context";
import { useTradesContext } from "@/features/accounts/shared/contexts/trades-context";
import { ErrorDialogContext } from "@/shared/context/error-context";
import { AddAccountGroupModal } from "@/features/accounts/account-group/components/add-account-group-modal";
import { AddNewAccountsModal } from "@/features/home/components/add-new-accounts-modal";
import { BaseRoundButton } from "@/shared/components/button/base-round-button";
import { SelectAccountsModal } from "@/features/accounts/shared/components/select-accounts-modal";
import { AddAccountInfoModal } from "@/features/accounts/finance/components/add-account-info-modal";
import { ConfirmAddAccountInfoModal } from "@/features/accounts/finance/components/confirm-add-account-info-modal";
import { Header } from "@/features/home/components/header"
import { Sidebar } from "@/features/home/components/side-bar"
import { ViewTrades } from "@/features/accounts/shared/pages/view-trades";
import { useViewTrades } from "@/features/accounts/shared/hooks/use-view-trades";
import { useInstitutionsContext } from "@/features/institutions/contexts/institutions-context";
import { InstitutionsReauthorizationModal } from "@/features/institutions/components/institutions-reauthorization-modal";
import { ReauthorizationCompleted } from "@/features/institutions/components/reauthorization-completed-modal";
import { useSelectAccounts } from "@/features/accounts/shared/hooks/use-select-accounts";
import { usePaymentContext } from "@/features/accounts/payment/scraping/contexts/payment-context";
import { useOcrPolling } from "@/features/accounts/ocr/hooks/use-ocr-polling";
import { useTradeJumpContext } from "@/features/trade-jump/contexts/trade-jump-context";
// import { GetTradeJumpInfoService } from "@/features/trade-jump/services/get-trade-jump-info-service";
import { useGetTradeJumpInfo } from "@/features/trade-jump/hooks/use-jump-trade";
import { useGetAccountGroupsShareInvited } from "@/features/accounts/account-group/hooks/use-get-account-groups-share-invited";
import { useAggregationPolling } from "@/features/accounts/shared/hooks/use-aggregation-polling";
import { InvitedGroupModal } from "@/features/accounts/account-group/components/invited-group-modal";
import { InvitationNavigation } from "@/features/my-data/components/invitation-navigation";
import { MoneyLinkWarning } from "@/shared/utils/custom-exception";
import { useGetFinanceServicesFinancetypes } from "@/features/accounts/finance/hooks/use-get-services-financetypes";
import { AccountsInfo } from "@/features/accounts/shared/hooks/use-select-accounts";
import { useSyncAccounts } from "@/features/accounts/shared/hooks/use-sync-accounts";
import { useLoginContext } from "@/features/auth/contexts/login-context";

export const Home = () => {
  const {
    soriId,
  } = useLoginContext();
  const {
    accountGroups,
    accounts,
    selectedAccountGroup,
    invitedAccountGroups,
    setUserSelectedAccountGroupId,
    setSelectedAccount,
  } = useAccountGroupsContext();
  const {
    openHomeModal,
    keepModal,
    setOpenHomeModal,
    setOpenMyDataModal,
  } = useModalContext();
  const {
    setInstitutionsServices,
    setSelectedInstitution,
    setSelectedInstitutionService,
    setAddinfoAccountsAccountGroupId,
    setAddinfoAccountsFinanceAccountId,
    resetAddinfoAccountsState,
  } = useInstitutionsContext();
  const {
    openTrades,
    setOpenTrades,
    resetTradesState
  } = useTradesContext();
  const {
    processType,
    setProcessType,
  } = usePaymentContext();
  const {
    tradeJumpMode,
    syncJump,
    jumpMlinkAccountId,
    jumpMlinkId,
    tradeJumpInfo,
    jumpFirstProcess,
    setTradeJumpMode,
    setJumpDetailSid,
    setJumpMlinkAccountId,
    setJumpMlinkId,
    setJumpStartDate,
    setJumpEndDate,
    setTradeJumpInfo,
    setJumpFirstProcess,
  } = useTradeJumpContext();
  const {
    registerFinances,
    registerCrecas,
    registerPayments,
    registerEcshops,
    getAllAccounts,
  } = useHome();
  const {
    initGetAccountGroupsCompleted,
    setInitGetAccountGroupsCompleted,
    refreshAccountGroups,
    setRefreshAccountGroups,
    refreshAccounts,
    setRefreshAccounts,
  } = useHomeStates();
  const { getAccountGroups } = useGetAccountGroups();
  const { updateAccountGroups } = useUpdateAccountGroups({ onTapCompleteModal: () => {} });
  const { updateTrades } = useViewTrades();
  const {
    resetLocalStorage,
  } = useSelectAccounts({onTapCompleteModal: () => {}});
  const { getAccountGroupsShareInvited } = useGetAccountGroupsShareInvited();
  const location = useLocation();
  const navigate = useNavigate();
  const { getTradeJumpInfo } = useGetTradeJumpInfo();
  const { getFinanceServicesFinancetypes } = useGetFinanceServicesFinancetypes();
  const { resetLocalStorageForSync } = useSyncAccounts({onTapCompleteModal: () => {}});

  // 認可後に取得された口座一覧
  const [beforeRegAccounts, setBeforeRegAccounts] = useState<AccountsInfo[]>([]);
  const [firstGetAccountGroups, setFirstGetAccountGroups] = useState(false);
  const { setError } = useContext(ErrorDialogContext);

  // OCRポーリング
  useOcrPolling();
  // アグリゲーションポーリング（銀行口座完了時に口座一覧を再取得して残高を最新化）
  useAggregationPolling(() => setRefreshAccountGroups((c) => c + 1));

  // 口座グループの選択を切り替えた際の処理
  const onChangeGroupId = async (accountGroupId: string) => {
    if(accountGroupId === "__FIXED_LAST__"){
      // 「新しい口座を追加」選択時の処理
      setOpenHomeModal("AddAccountGroup");
      return;
    }
    await updateAccountGroups(
      accountGroupId,
      undefined,
      true,
      undefined,
    );
    setUserSelectedAccountGroupId(accountGroupId);
    setRefreshAccountGroups(refreshAccountGroups + 1);
  };

  useEffect(() => {
    // 別タブで別アカウント表示時の画面同期用のモーダルリセット処理

    // 画面描画直後(soriIdとsyncJumpが初期値)の際はスキップ
    if (soriId === "") return;
    if (syncJump === undefined) return;
    // モーダル保持指定時もスキップ
    if (keepModal) return;
    setOpenHomeModal(undefined);
    setOpenMyDataModal(undefined);
    setOpenTrades(false);
  }, [soriId, syncJump]);

  useEffect(() => {
    // 初回口座グループ取得完了していない場合はスキップ
    if (!initGetAccountGroupsCompleted) return;
    setRefreshAccountGroups(refreshAccountGroups + 1);
  }, [soriId, syncJump]);

  // 共有招待されている口座グループ情報の取得
  useEffect(() => {
    if (syncJump || syncJump === undefined) return;
    getAccountGroupsShareInvited();
  }, [soriId, syncJump]);

  // 招待されている口座グループが更新された際の処理
  useEffect(() => {
    if (syncJump) return;
    if (invitedAccountGroups.length === 0) return;
    if (location.search) return;
    if (openHomeModal) return;
    setOpenHomeModal("ShareInvited");
  }, [invitedAccountGroups, syncJump]);

  // 口座グループ取得処理
  useEffect(() => {
    const selectAccountGroup = async () => {
      await getAccountGroups(true);
      setInitGetAccountGroupsCompleted(true);
      setRefreshAccounts(refreshAccounts + 1);
      setFirstGetAccountGroups(true);
    };
    selectAccountGroup();
  }, [refreshAccountGroups]);

  // 口座全件取得処理
  useEffect(() => {
    // 初回口座グループ取得完了していない場合はスキップ
    if (!initGetAccountGroupsCompleted) return;
    getAllAccounts(selectedAccountGroup.accountGroupId);
  }, [refreshAccounts]);

  // 明細ジャンプ開始処理
  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    // 明細ジャンプのエンドポイントは下記
    // /home?jump=<jump_type>&passbooksid=<id>&detailsid=<id>&tabletype=<type>
    const jumpType = queryParams.get("jump");
    const passbookSid = queryParams.get("passbooksid");
    const detailSid = queryParams.get("detailsid");
    const tableType = queryParams.get("tabletype");
    // 明細ジャンプの処理
    if (jumpType !== null && passbookSid !== null && detailSid !== null && tableType !== null) {
      setTradeJumpMode(true);
      setJumpDetailSid(detailSid);
      const TradeJumpInfo = async () => {
          const res = await getTradeJumpInfo(passbookSid, detailSid, tableType);
          if (res) {
            setTradeJumpInfo(res);
          }
      };
      TradeJumpInfo();
    }
  }, []);

  // 明細ジャンプ対象口座グループ決定処理
  useEffect(() => {
    if (!tradeJumpMode) return;
    if (tradeJumpInfo === undefined) return;
    if (!jumpFirstProcess) return;
    const jumpMatchAccountGroupId = accountGroups.find(group => group.accountGroupId === tradeJumpInfo.account_group_id);
    if (jumpMatchAccountGroupId !== undefined) {
      onChangeGroupId(jumpMatchAccountGroupId.accountGroupId);
      setJumpMlinkAccountId(tradeJumpInfo.mlink_account_id);
      setJumpMlinkId(tradeJumpInfo.mlink_id);
      setJumpStartDate(tradeJumpInfo.set_start_date.replace(/-/g, ""));
      setJumpEndDate(tradeJumpInfo.set_end_date.replace(/-/g, ""));
    }
  }, [tradeJumpInfo, firstGetAccountGroups]);

  // 明細ジャンプ対象口座決定処理
  useEffect(() => {
    if (!tradeJumpMode) return;
    if (jumpMlinkAccountId === undefined) return;
    if (jumpMlinkId === undefined) return;
    const jumpMatchAccount = accounts.find(account => account.mlinkAccountId === jumpMlinkAccountId && account.mlinkId === jumpMlinkId);
    if (jumpMatchAccount !== undefined && jumpFirstProcess) {
      setSelectedAccount(jumpMatchAccount);
      setJumpFirstProcess(false);
      setOpenTrades(true);
    }
  }, [jumpMlinkAccountId, jumpMlinkId, accounts]);

  // 認可エラータイプに応じたエラーメッセージ取得
  const getAuthorizationErrorMessage = (errorType: string): string => {
    switch (errorType) {
      // callback.py由来のエラー
      case "param":
      case "badrecord":
      case "unexpected":
      case "missing_logininfosid":
        return "口座登録に失敗しました。時間をおいて再度お試しください。";
      // authorization_callback.py由来のエラー
      case "moneylinkbridge_user_operation_error":
        return "誤った操作や入力が検知されました。考えられる要因は以下です。\n・アプリとのAPI連携を拒否するボタンを押下した。\n・認証に失敗した";
      case "moneylinkbridge_other_system_already_registered":
        return "今認証を行ったアカウントは、既にシステムに登録済みです。";
      case "moneylinkbridge_other_system_user_mistake":
        return "登録されているアカウント情報と異なる情報が入力されたため、操作が中断されました。\n恐れ入りますが当画面を閉じてMoneyLinkから再度接続を開始し、正しいアカウント情報をご入力ください。";
      case "moneylinkbridge_other_system_error":
      case "moneylinkbridge_required_reauth":
      case "moneylinkbridge_contract_canceled":
      case "moneylinkbridge_server_not_found":
      case "moneylinkbridge_mlapi_error":
      case "moneylinkbridge_mlapiservice_system_error":
        return "このサービスとの接続中に予期せぬエラーが発生しました。\n時間をおいてMoneyLinkから再度接続頂くか、サービスのマイページ等にログイン・操作ができるかご確認ください";
      case "moneylinkbridge_mlapi_operation_check":
        return "接続設定についてエラーが発生しました。\n恐れ入りますがサービスのマイページ等のログイン・ログアウト操作や、アカウント情報について設定をご確認ください。";
      case "moneylinkbridge_mlapiservice_closed":
      case "moneylinkbridge_mlapiservice_maintenance":
        return "現在接続中のサービスがメンテナンス中もしくは通信時間外のため、接続が中断された可能性があります。\n恐れ入りますが、しばらく時間をおいてから再度ご利用いただくか、マイページ等にログイン・操作ができるかご確認ください。";
      default:
        return "予期せぬエラーが発生しました。MoneyLinkとの接続を開始できません。\n恐れ入りますが、しばらく時間をおいてから再度ご利用ください。";
    }
  };

  // 口座登録関連リダイレクト後処理
  useEffect(() => {
    const registrationProcess = async () => {
      const queryParams = new URLSearchParams(location.search);
      const accountGroupId = queryParams.get("account_group_id");
      const financeAccountId = queryParams.get("finance_account_id");
      const crecaAccountId = queryParams.get("creca_account_id");
      const paymentAccountId = queryParams.get("payment_account_id");
      const ecshopAccountId = queryParams.get("ecshop_account_id");
      const reauth = queryParams.get("reauth");
      const error = queryParams.get("error");

      let returnFlg = true;
      let matchCount = 0;
      if (accountGroupId) {
        if (financeAccountId) { returnFlg = false; matchCount += 1; }
        if (crecaAccountId) { returnFlg = false; matchCount += 1; }
        if (paymentAccountId) { returnFlg = false; matchCount += 1; }
        if (ecshopAccountId) { returnFlg = false; matchCount += 1; }
      }
      if (reauth) { returnFlg = false; matchCount += 1; }
      if (error) { returnFlg = false; matchCount += 1; }

      // 複数該当は想定外のためreturn対象
      if (matchCount > 1) { returnFlg = true; }
  
      // return
      if (returnFlg) { return; }

      const mitsubishiUfjCorp = localStorage.getItem("MitsubishiUfjCorp") || "";
      // リダイレクト時の処理
      if (accountGroupId) {
        let res = undefined;
        // 銀行API口座登録処理
        if (financeAccountId) {
          if (mitsubishiUfjCorp === "1") {
            // 三菱UFJ[法人] 専用処理
            setAddinfoAccountsAccountGroupId(accountGroupId);
            setAddinfoAccountsFinanceAccountId(financeAccountId);
            await getFinanceServicesFinancetypes(
              localStorage.getItem("industryCode") || "",
              localStorage.getItem("companyCode") || "",
              localStorage.getItem("serviceCode") || "",
            );
            setOpenHomeModal("AddAccountInfo");
          } else {
            res = await registerFinances(accountGroupId, financeAccountId);
          }
        }
        // クレカAPI口座登録処理
        if (crecaAccountId) {
          res = await registerCrecas(accountGroupId, crecaAccountId);
        }
        // Look口座登録処理
        if (paymentAccountId) {
          res = await registerPayments(accountGroupId, paymentAccountId);
        }
        // カラーミー口座登録処理
        if (ecshopAccountId) {
          res = await registerEcshops(accountGroupId, ecshopAccountId);
        }
        if (res) {
          setBeforeRegAccounts(res);
          setOpenHomeModal("SelectAccounts");
        } else if (mitsubishiUfjCorp === "1") {
          //
        } else {
          // 口座登録エラー時の処理
          setError(new MoneyLinkWarning({
            title: "エラー",
            message: "口座登録に失敗しました。時間をおいて再度お試しください。",
          }));
        }
      }
      if (reauth) {
        if (reauth === "completed") {
          setOpenHomeModal("ReauthCompleted");
        }
      }
      // 認可失敗によるエラー処理
      if (error) {
        // TODO:? errorにはエラータイプが格納されているため分岐処理は可能
        const errorMessage = getAuthorizationErrorMessage(error);
        setError(new MoneyLinkWarning({
          title: "認可エラー",
          message: errorMessage,
        }));
      }
    };
    registrationProcess();

    // エンドポイントから口座情報(account_group_idなど)を消す
    navigate("/home", { replace: true });
  }, [location.search]);


  return (
    <>
      <Header
        doAction={() => {
          setRefreshAccountGroups(refreshAccountGroups + 1);
        }}
      />
      <Sidebar />
      <InvitationNavigation />
      <main>
        <section className="home_lists">
          <AccountsList
            accountGroups={accountGroups}
            accounts={accounts}
            isOwner={selectedAccountGroup.isOwner === 1}
            onListSelect={(groupId) => {onChangeGroupId(groupId)}}
            onSelectElement={() => {
              setOpenHomeModal(undefined);
              setOpenTrades(true)
            }}
          />

          <nav id="addNewList">
            {!syncJump && selectedAccountGroup.isOwner === 1 && (
            <BaseRoundButton
                className="open-modal btn-add"
                buttonText="金融機関を追加する"
                onClick={() => {
                  //openSelAccountType();
                  setOpenHomeModal("AddNewAccounts");
                }}
            />
            )}
          </nav>
        </section>

        <AddNewAccountsModal
          onTapCloseModal={() => {
            setOpenHomeModal(undefined);
            setSelectedInstitution(undefined);
            setSelectedInstitutionService(undefined);
            setInstitutionsServices([]);
          }}
          onTapCompleteModal={async(paymentAccountId) => {
            if (paymentAccountId) {
              if (processType === "reg") {
                const res = await registerPayments(
                  selectedAccountGroup.accountGroupId,
                  paymentAccountId,
                )
                if (res) {
                  setBeforeRegAccounts(res);
                  setOpenHomeModal("SelectAccounts");
                } else {
                  // 口座なしエラー時の処理
                }
              } else if (processType === "upd") {
                setOpenHomeModal("ReauthCompleted");
                setOpenTrades(false);
              }
              setProcessType("");
            }
            else {
              setOpenHomeModal(undefined);
              setRefreshAccountGroups(refreshAccountGroups + 1);
            }
          }}
        />
        
        {openHomeModal === "SelectAccounts" && (
          <SelectAccountsModal
            accounts={beforeRegAccounts}
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
              setSelectedInstitution(undefined);
              setSelectedInstitutionService(undefined);
              resetLocalStorage();
            }}
            onTapCompleteModal={(lists, mitsubishiUfjCorp) => {
              // ワンタイムパスが発生し得る口座が1件でもある場合はupdateTradesに進まない
              const hasOtpAccount = lists.some((list) => list.scrapeType === 2 && list.otpYn !== "0");
              if (hasOtpAccount) {
                setError(new MoneyLinkWarning({
                  title: "ワンタイムパスワードの入力",
                  message: "Web明細帳の作成が完了しましたが、明細取込時にワンタイムパスワードの入力が必要な場合がございます。明細を取り込むには口座の明細一覧画面から操作を行ってください。",
                }));
                setOpenHomeModal(undefined);
                setSelectedInstitution(undefined);
                setSelectedInstitutionService(undefined);
                setRefreshAccountGroups(refreshAccountGroups + 1);
                resetLocalStorage();
                return;
              }
              // ワンタイムパスが発生しないもののみ自動で更新処理につなげる
              for (const list of lists) {
                updateTrades(
                  list.accountGroupId,
                  list.mlinkAccountId,
                  list.mlinkId,
                  list.passbookType,
                  list.scrapeType,
                );
              }
              if (mitsubishiUfjCorp === "1") {
                setOpenHomeModal("ConfirmAddAccountInfo");
              } else {
                setOpenHomeModal(undefined);
                setSelectedInstitution(undefined);
                setSelectedInstitutionService(undefined);
                setRefreshAccountGroups(refreshAccountGroups + 1);
                resetLocalStorage();
              }
            }}
          />
        )}

        {openHomeModal === "AddAccountInfo" && (
          <AddAccountInfoModal
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
              setSelectedInstitution(undefined);
              setSelectedInstitutionService(undefined);
              resetLocalStorage();
              resetAddinfoAccountsState();
            }}
            onTapCompleteModal={(accounts) => {
              setBeforeRegAccounts(accounts);
              setOpenHomeModal("SelectAccounts");
            }}
          />
        )}

        {openHomeModal === "ConfirmAddAccountInfo" && (
          <ConfirmAddAccountInfoModal
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
              setSelectedInstitution(undefined);
              setSelectedInstitutionService(undefined);
              setRefreshAccountGroups(refreshAccountGroups + 1);
              resetLocalStorage();
            }}
            onTapCompleteModal={() => {
              setOpenHomeModal("AddAccountInfo");
            }}
          />
        )}

        {openHomeModal === "SiteReauth" && (
          <InstitutionsReauthorizationModal
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
            }}
          />
        )}

        {openHomeModal === "ReauthCompleted" && (
          <ReauthorizationCompleted
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
            }}
          />
        )}

        {/* 明細一覧画面 */}
        {openTrades &&
        <ViewTrades
          isOwner={selectedAccountGroup.isOwner === 1}
          onTapClose={(refresh) => {
            setOpenTrades(false);
            setInstitutionsServices([]);
            setSelectedInstitutionService(undefined);
            resetTradesState();
            resetLocalStorageForSync();
            setJumpDetailSid(undefined);
            if (refresh) {
              setRefreshAccountGroups(refreshAccountGroups + 1);
            }
          }}
          deleteAccountProc={() => {
            setOpenTrades(false);
            setInstitutionsServices([]);
            setSelectedInstitutionService(undefined);
            resetTradesState();
            resetLocalStorageForSync();
            setRefreshAccountGroups(refreshAccountGroups + 1);
          }}
          afterAddAccountProc={(addList) => {
            setOpenTrades(false);
            resetLocalStorageForSync();
            setRefreshAccountGroups(refreshAccountGroups + 1);
            for (const list of addList) {
                updateTrades(
                  list.accountGroupId,
                  list.mlinkAccountId,
                  list.mlinkId,
                  list.passbookType,
                  list.scrapeType,
                );
              }
          }}
        />
        }

        {openHomeModal === "AddAccountGroup" && (
          <AddAccountGroupModal
            onTapCloseModal={() => {
              setOpenHomeModal(undefined);
              setRefreshAccountGroups(refreshAccountGroups + 1);
            }}
            onTapCompleteModal={() => {
              setOpenHomeModal(undefined);
              setRefreshAccountGroups(refreshAccountGroups + 1);
            }}
          />
        )}

        {openHomeModal === "ShareInvited" && (
          <InvitedGroupModal
            onTapCloseModal={() => {
              setRefreshAccountGroups(refreshAccountGroups + 1);
              setOpenHomeModal(undefined);
            }}
          />
        )}
      </main>
    </>
  );
};
